<template>
	<div class="settings-container">
		<h1>{{ $t('appbar.settings-menu.ManageProjects.title') }}</h1>
		<b-form-group :label="$t('appbar.settings-menu.ManageProjects.selection')" label-for="project-selection">
			<b-form-select id="project-selection" :options="availableProjects" @change="getProjectDetails($event)" />
		</b-form-group>
		<b-tabs content-class="p-32">
			<b-tab :title="$t('appbar.settings-menu.ManageProjects.info')" active>
				<b-form>
					<b-form-group :label="$t('appbar.settings-menu.ManageProjects.name')" label-for="name">
						<b-form-input id="name" v-model="selectedProject.name" />
					</b-form-group>
					<b-form-group :label="$t('appbar.settings-menu.ManageProjects.tagline')" label-for="tagline">
						<b-form-textarea id="tagline" v-model="selectedProject.tagline" rows="3" />
					</b-form-group>
					<b-form-group :label="$t('appbar.settings-menu.ManageProjects.description')" label-for="description">
						<b-form-textarea id="description" v-model="selectedProject.description" rows="7" />
					</b-form-group>
					<b-form-group :label="$t('appbar.settings-menu.ManageProjects.location')" label-for="location">
						<b-form-input id="location" v-model="selectedProject.location" rows="7" />
					</b-form-group>
					<div>
						{{ $t('appbar.settings-menu.ManageProjects.logo') }}
						<UserFile id="project-logo" :file-uri="logoImage" @fileSelected="setProjectLogo" />
					</div>
					<b-form-group :label="$t('appbar.settings-menu.ManageProjects.units')" label-for="units">
						<b-form-select id="units" v-model="selectedProject.units" :options="unitOptions" />
					</b-form-group>
					<b-form-group :label="$t('appbar.settings-menu.ManageProjects.currency')" label-for="currency">
						<b-form-select id="currency" :options="currencyOptions" />
					</b-form-group>
					<b-form-group>
						<b-form-row>
							<b-col>
								<b-button variant="primary" :disabled="!selectedProject.id" @click="saveProjectInformation">
									{{ $t('appbar.settings-menu.ManageProjects.save') }}
								</b-button>
								<b-button :disabled="!selectedProject.id" @click="getProjectDetails(selectedProject.id)">
									{{ $t('appbar.settings-menu.ManageProjects.cancel') }}
								</b-button>
							</b-col>
							<b-col class="d-flex justify-content-end">
								<b-button variant="danger" :disabled="!selectedProject.id" @click="deleteProject">
									{{ $t('appbar.settings-menu.ManageProjects.delete') }}
								</b-button>
							</b-col>
						</b-form-row>
					</b-form-group>
				</b-form>
			</b-tab>
			<b-tab :title="$t('appbar.settings-menu.ManageProjects.roster')">
				<b-table
					striped
					outlined
					table-class="roster"
					tfoot-class="roster-footer"
					:fields="projectRosterFields"
					:items="projectRosterPeople"
					:sort-compare="sortCompare"
				>
					<template #cell(icon)>
						<svg class="person-icon" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
							<path fill="#666666" stroke="#666666" d="M500 0C776.1 0 1000 223.9 1000 500 1000 776.1 776.1 1000 500 1000 223.9 1000 0 776.1 0 500 0 223.9 223.9 0 500 0ZM500 464.3C326.1 464.3 304.6 542.4 302 646.2L301.6 671.6 301.6 705.6C353 755.2 422.9 785.7 500 785.7 573.2 785.7 640 758.2 690.6 712.9L698.4 705.6 698.4 672.1C697.9 555.7 688 464.3 500 464.3ZM500 214.3L493.7 214.5C437.5 217.7 392.9 264.4 392.9 321.4 392.9 380.6 440.8 428.6 500 428.6 559.2 428.6 607.1 380.6 607.1 321.4 607.1 264.4 562.5 217.7 506.3 214.5L500 214.3Z" />
						</svg>
					</template>
					<template #cell(person)="data">
						<div class="name">
							{{ data.item.firstName }} {{ data.item.lastName }}
						</div>
						<span class="work-info">{{ data.item.title }} | {{ data.item.enterprises[0].name }}</span>
					</template>
					<template #cell(role)="data">
						<div class="name">
							{{ data.item.enterprises[0].enterprise_user.role }}
						</div>
					</template>
					<template #cell(projectManager)="data">
						<div v-if="['OWNER','ADMIN'].includes(data.item.enterprises[0].enterprise_user.role)">
							<b-form-checkbox
								v-model="data.item.projectManager"
								disabled
							/>
						</div>
						<div v-else>
							<b-form-checkbox
								v-model="data.item.projectManager"
								@change="setProjectManager(data.item)"
							/>
						</div>
					</template>
					<template #cell(remove)="data">
						<b-button variant="danger" @click="showRemoveModal(data)">
							{{ $t('appbar.settings-menu.ManageProjects.remove') }}
						</b-button>
					</template>
					<template #custom-foot>
						<b-tr>
							<b-td colspan="3">
								<b-form-select id="user-selection" v-model="userToAdd" :options="enterpriseRosterPeople" />
							</b-td>
							<b-td>
								<b-button class="d-flex justify-content-start" @click="addProjectUser">
									{{ $t('appbar.settings-menu.ManageProjects.add') }}
								</b-button>
							</b-td>
							<b-td>
								<b-button v-b-toggle.invite-menu class="d-flex justify-content-end">
									{{ $t('appbar.settings-menu.ManageProjects.openInviteMenu') }}
								</b-button>
							</b-td>
						</b-tr>
					</template>
				</b-table>
				<b-sidebar id="invite-menu" :title="$t('appbar.settings-menu.ManageProjects.inviteHeading')" right>
					<b-form class="p-3">
						<p>{{ $t('appbar.settings-menu.ManageProjects.inviteDescription') }}</p>
						<b-form-group :label="$t('appbar.settings-menu.ManageProjects.email')" label-for="invite-email">
							<b-form-input id="invite-email" v-model="inviteForm.email" type="email" />
						</b-form-group>
						<b-form-group :label="$t('appbar.settings-menu.ManageProjects.inviteFirstName')" label-for="invite-first-name">
							<b-form-input id="invite-first-name" v-model="inviteForm.firstName" type="text" />
						</b-form-group>
						<b-form-group :label="$t('appbar.settings-menu.ManageProjects.inviteLastName')" label-for="invite-last-name">
							<b-form-input id="invite-last-name" v-model="inviteForm.lastName" type="text" />
						</b-form-group>
					</b-form>
					<template #footer="{ hide }">
						<b-button variant="danger" @click="hide">
							{{ $t('appbar.settings-menu.ManageProjects.cancel') }}
						</b-button>
						<b-button variant="primary" @click="inviteUserToEnterprise">
							{{ $t('appbar.settings-menu.ManageProjects.submitInvite') }}
						</b-button>
					</template>
				</b-sidebar>
			</b-tab>
		</b-tabs>
	</div>
</template>

<script>
import UserFile from '@/components/Files/UserFile.vue';

export default {
	components: {
		UserFile,
	},
	data() {
		return {
			availableProjects: [],
			availableUsers: [],
			selectedProject: {},
			currencyOptions: [{ text: this.$t('appbar.settings-menu.ManageProjects.dollars'), value: 'dollars' }],
			projectRosterFields: [
				{ key: 'icon', label: '' },
				{ key: 'person', label: this.$t('appbar.settings-menu.ManageProjects.name'), sortable: true },
				{ key: 'role', label: this.$t('appbar.settings-menu.UserInformation.role'), sortable: true },
				{
					key: 'projectManager',
					label: this.$t('appbar.settings-menu.ManageProjects.projectManager'),
					sortable: true,
				},
				{ key: 'remove', label: '' },
			],
			projectRosterPeople: [],
			enterpriseRosterPeople: [],
			userToAdd: null,
			logoImage: null,
			previousLogoImage: null,
			updateLogoFile: false,
			timeFormatOptions: [
				{ text: this.$t('appbar.settings-menu.ManageProjects.twelveHour'), value: 'twelveHour' },
				{ text: this.$t('appbar.settings-menu.ManageProjects.twentyfourHour'), value: 'twentyfourHour' },
			],
			unitOptions: [
				{ text: this.$t('appbar.settings-menu.ManageProjects.imperial'), value: 'IMPERIAL' },
				{ text: this.$t('appbar.settings-menu.ManageProjects.metric'), value: 'METRIC' },
			],
			inviteForm: {
				email: null,
				firstName: null,
				lastName: null,
			},
		};
	},
	created() {
		this.initializeProjectList();
		const enterpriseId = this.$store.state.user.currentEnterprise.id;
		this.axios.get(`/enterprises/${enterpriseId}/users`).then((data) => {
			for (const user of data.data.users) {
				this.availableUsers.push(user);
				this.enterpriseRosterPeople.push({
					value: user.id,
					text: `${user.firstName} ${user.lastName}, ${user.title}`,
				});
			}
		});
	},
	methods: {
		initializeProjectList() {
			this.availableProjects = [];
			this.selectedProject = {};
			this.axios.get('/projects').then((data) => {
				for (const project of data.data) {
					this.availableProjects.push({ text: project.name, value: project.id });
				}
			});
		},
		async showRemoveModal(data) {
			const confirmation = await this.$msgBoxConfirm({
				message: this.$t('appbar.settings-menu.ManageProjects.removeBody', {
					name: `${data.item.firstName} ${data.item.lastName}`,
					project: this.selectedProject.name,
				}),
				okTitle: this.$t('appbar.settings-menu.ManageProjects.remove'),
				title: this.$t('appbar.settings-menu.ManageProjects.removeHeading', { name: data.item.name }),
			});
			if (confirmation) {
				await this.removeProjectUser(data.item.id, `${data.item.firstName} ${data.item.lastName}`);
			}
		},
		sortCompare(a, b, key, _sortDesc, _formatter, compareOptions, compareLocale = 'en') {
			if (key === 'person') return a.lastName.localeCompare(b.lastName, compareLocale, compareOptions);
			if (key === 'role')
				return a.enterprises[0].enterprise_user.role.localeCompare(
					b.enterprises[0].enterprise_user.role,
					compareLocale,
					compareOptions
				);
			if (key === 'projectManager') return (b.projectManager === false) - (a.projectManager === false);
			return null;
		},
		async saveProjectInformation() {
			const response = await this.axios.patch(`/projects/${this.selectedProject.id}`, this.selectedProject);
			// needs error handling
			await this.getProjectDetails(this.selectedProject.id);
			if (response.status === 200) {
				if (this.updateLogoFile && this.selectedProject.data.logo) {
					// call the tags endpoint for the photo to save the link to this project
					const fileId = this.selectedProject.data.logo.replace('dmp:file:', '');
					this.axios
						.post(`/files/${fileId}/tags`, [`dmp:project:${this.selectedProject.id}`])
						.catch((error) => {
							console.error(error);
						});
					if (this.previousLogoImage) {
						this.axios
							.delete(
								`/files/${this.previousLogoImage.replace('dmp:file:', '')}/tags/dmp:project:${
									this.selectedProject.id
								}`
							)
							.catch((error) => {
								console.error(error);
							});
					}
				}
				this.$store.dispatch('fetchProjectList');
				this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.saved'), {
					variant: 'success',
					title: 'Success',
				});
			} else {
				this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
					title: 'Error',
					variant: 'danger',
				});
			}
		},
		async getProjectDetails(projectId) {
			try {
				const response = await this.axios.get(`/projects/${projectId}/users`);
				this.selectedProject = response.data;
				if (this.selectedProject.data && this.selectedProject.data.logo) {
					this.logoImage = this.selectedProject.data.logo;
				} else {
					this.logoImage = null;
				}
				this.projectRosterPeople = [];
				for (const user of response.data.users) {
					if (
						user.project_user.isProjectManager ||
						['OWNER', 'ADMIN'].includes(user.enterprises[0].enterprise_user.role)
					) {
						user.projectManager = true;
					} else {
						user.projectManager = false;
					}
					this.projectRosterPeople.push(user);
				}
				return true;
			} catch (error) {
				return false;
			}
		},
		async setProjectManager(data) {
			try {
				const response = await this.axios
					.put(`/projects/${this.selectedProject.id}/users/${data.id}`, {
						isProjectManager: data.projectManager,
						expirationDate: data.project_user.expirationDate,
					})
					.catch(() => {
						this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
							title: 'Error',
							variant: 'danger',
						});
						return false;
					});
				if (response.status === 200) {
					let message = data.projectManager ? 'granted' : 'revoked';
					message = `${data.firstName} ${data.lastName} ${this.$t(
						`appbar.settings-menu.ManageProjects.actions.${message}`
					)}`;
					this.$bvToast.toast(`${message}`, {
						title: this.$t('appbar.labels.success'),
						variant: 'success',
					});
				} else {
					this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
						title: this.$t('appbar.labels.error'),
						variant: 'danger',
					});
				}
				return true;
			} catch (error) {
				this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
					title: this.$t('appbar.labels.error'),
					variant: 'danger',
				});
				return false;
			}
		},
		async removeProjectUser(userId, userName) {
			try {
				const response = await this.axios.delete(`/projects/${this.selectedProject.id}/users/${userId}`);
				await this.getProjectDetails(this.selectedProject.id);
				const message = `${userName} ${this.$t('appbar.settings-menu.ManageProjects.actions.removed')}`;
				if (response.status === 204) {
					this.$bvToast.toast(`${message}`, { variant: 'success', title: this.$t('appbar.labels.success') });
				} else {
					this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
						title: this.$t('appbar.labels.error'),
						variant: 'danger',
					});
				}
			} catch (error) {
				this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
					title: this.$t('appbar.labels.error'),
					variant: 'danger',
				});
			}
		},
		async addProjectUser() {
			try {
				const response = await this.axios.put(`/projects/${this.selectedProject.id}/users/${this.userToAdd}`);
				await this.getProjectDetails(this.selectedProject.id);
				if (response.status === 200) {
					this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.added'), {
						title: this.$t('appbar.labels.success'),
						variant: 'success',
					});
				} else {
					this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
						title: this.$t('appbar.labels.error'),
						variant: 'danger',
					});
				}
			} catch (error) {
				this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
					title: this.$t('appbar.labels.error'),
					variant: 'danger',
				});
			}
		},
		async deleteProject() {
			try {
				const confirmation = await this.$msgBoxConfirm({
					message: this.$t('appbar.settings-menu.ManageProjects.deleteBody', {
						project: this.selectedProject.name,
					}),
					okTitle: this.$t('appbar.settings-menu.ManageProjects.delete'),
					title: this.$t('appbar.settings-menu.ManageProjects.deleteHeading'),
				});
				if (confirmation) {
					const response = await this.axios.delete(`/projects/${this.selectedProject.id}`);
					if (response.status === 204) {
						this.$store.commit('setProject', null);
						this.$store.dispatch('fetchProjectList');
						this.initializeProjectList();
						this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.deleted'), {
							title: this.$t('appbar.labels.success'),
							variant: 'success',
						});
					}
				}
			} catch (error) {
				this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
					title: this.$t('appbar.labels.error'),
					variant: 'danger',
				});
			}
		},
		async inviteUserToEnterprise() {
			try {
				this.$root.$emit('bv::toggle::collapse', 'invite-menu');
				const enterpriseId = this.$store.state.user.currentEnterprise.id;
				const data = {};
				if (this.inviteForm.lastName || this.inviteForm.firstName) {
					data.firstName = this.inviteForm.firstName;
					data.lastName = this.inviteForm.lastName;
				}
				this.axios
					.post(
						`/enterprises/${enterpriseId}/projects/${this.selectedProject.id}/invite/${this.inviteForm.email}`,
						data
					)
					.then((response) => {
						if (response.status === 201) {
							this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.inviteSent'));
						} else if (response.status === 200) {
							this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.inviteExisting'));
						}
						this.getProjectDetails(this.selectedProject.id);
					})
					.catch((error) => {
						if (error.response.status === 409) {
							this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.invitePendingUser'), {
								variant: 'warning',
								title: this.$t('appbar.labels.warning'),
							});
						} else {
							this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
								title: this.$t('appbar.labels.error'),
								variant: 'danger',
							});
						}
					});
			} catch (error) {
				this.$bvToast.toast(this.$t('appbar.settings-menu.ManageProjects.actions.failed'), {
					title: this.$t('appbar.labels.error'),
					variant: 'danger',
				});
			}
		},
		setProjectLogo(fileId) {
			if (this.selectedProject) {
				if (!this.selectedProject.data) this.selectedProject.data = {};
				// the below should pull from the backend for the active project,
				// as a user switching logos multiple times before saving will result
				// in a reference to the wrong file.
				// this should be revisited when taking a comprehensive look at how
				// we are linking disparate objects in the future.
				if (!this.previousLogoImage && this.selectedProject.data.logo)
					this.previousLogoImage = this.selectedProject.data.logo;
				this.selectedProject.data.logo = fileId;
				this.updateLogoFile = true;
			}
		},
	},
};
</script>

<style scoped>
.settings-container {
	flex: 1;
	overflow-y: auto;
	padding: 1rem;
}

.settings-container::after {
	content: '';
	display: block;
	height: 1rem;
	width: 100%;
}

.logo {
	position: relative;
	border: 1px solid #323232;
	cursor: pointer;
	height: 150px;
	width: 250px;
}

.tan-icon-arrow-up {
	position: absolute;
	right: 5px;
	bottom: 5px;
}

.logo-input {
	display: none;
}

.person-icon {
	width: 2rem;
}

.name {
	color: #697781;
	font-size: 1.2rem;
}

.work-info {
	color: #999999;
	font-size: 0.9rem;
}
</style>

<style>
.roster td {
	vertical-align: middle !important;
}

.roster-footer {
	text-align: center;
}
</style>